import {
  servifinExportRows,
  servifinExportFilename,
  servifinExportHeaderAoA,
  servifinLabels,
} from 'app/shared/servifin-export.util';
import { loadServifinLogoBase64 } from 'app/shared/servifin-logo.util';
import { environment } from 'environments/environment';
import * as XLSX from 'xlsx';

function escapeHtml(value: any): string {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function jsonToHtmlTable(rows: Record<string, any>[]): string {
  if (!rows.length) {
    return '<table border="1" cellpadding="4"><tr><td>Sin datos</td></tr></table>';
  }
  const keys = Object.keys(rows[0]);
  let html = '<table border="1" cellpadding="4" cellspacing="0"><thead><tr>';
  keys.forEach(key => {
    html += `<th>${escapeHtml(key)}</th>`;
  });
  html += '</tr></thead><tbody>';
  rows.forEach(row => {
    html += '<tr>';
    keys.forEach(key => {
      html += `<td>${escapeHtml(row[key])}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table>';
  return html;
}

function buildBrandedExcelHtml(logoBase64: string, tableHtml: string): string {
  const ts = new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' });
  const logoImg = logoBase64
    ? `<img src="${logoBase64}" height="55" alt="Servifin S.A.S"/>`
    : '';
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8">
<style>
body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#222}
.hdr td{vertical-align:middle;padding:6px 8px}
.brand{font-size:14px;font-weight:bold;color:#2E7D32}
.tagline{font-size:11px;color:#003893}
th{background:#f0f0f0;font-weight:bold;text-align:center}
td,th{border:1px solid #ccc;padding:4px}
</style></head><body>
<table class="hdr" width="100%"><tr>
<td width="130">${logoImg}</td>
<td>
<div class="brand">${escapeHtml(servifinLabels.empresa)}</div>
<div class="tagline">${escapeHtml(servifinLabels.tagline)}</div>
<div>${escapeHtml(servifinLabels.ciudad)}, ${escapeHtml(servifinLabels.pais)}</div>
<div style="font-size:10px;color:#666">Generado: ${escapeHtml(ts)}</div>
</td></tr></table><br/>${tableHtml}</body></html>`;
}

function downloadHtmlAsExcel(html: string, filename: string): void {
  const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = servifinExportFilename(filename);
  anchor.click();
  URL.revokeObjectURL(url);
}

function prependServifinHeader(ws: XLSX.WorkSheet): XLSX.WorkSheet {
  const header = servifinExportHeaderAoA();
  if (!header.length) {
    return ws;
  }
  const body = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
  return XLSX.utils.aoa_to_sheet([...header, ...body]);
}

function writeServifinWorkbook(wb: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(wb, servifinExportFilename(filename));
}

function exportBrandedExcel(tableHtml: string, filename: string): void {
  loadServifinLogoBase64().then(logo => {
    downloadHtmlAsExcel(buildBrandedExcelHtml(logo, tableHtml), filename);
  });
}

export function exportJsonToExcel(
  rows: Record<string, any>[],
  sheetName: string,
  fileBase: string,
  exactFilename?: string
): void {
  const data = servifinExportRows(rows);
  const fecha = new Date().toISOString().substring(0, 10);
  const filename = exactFilename || `${fileBase}${fecha}.xlsx`;

  if (environment.SERVIFIN_MODE) {
    exportBrandedExcel(jsonToHtmlTable(data), filename);
    return;
  }

  const ws = prependServifinHeader(XLSX.utils.json_to_sheet(data));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  writeServifinWorkbook(wb, filename);
}

export function exportHtmlTableToExcel(
  tableElement: HTMLElement,
  sheetName: string,
  filename: string
): void {
  if (environment.SERVIFIN_MODE) {
    exportBrandedExcel(tableElement.outerHTML, filename);
    return;
  }

  const ws = prependServifinHeader(XLSX.utils.table_to_sheet(tableElement));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  writeServifinWorkbook(wb, filename);
}

export { servifinLabels, servifinExportFilename };
